import {
  Children,
  ComponentProps,
  isValidElement,
  cloneElement,
  FC,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  forwardRef,
  type CSSProperties,
  useId,
} from 'react';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { isElement } from 'react-is';
import reactToText from 'react-to-text';

import {
  containerClass,
  indicatorClass,
  indicatorHiddenClass,
  indicatorTransformVar,
  indicatorWidthVar,
  itemClass,
  labelClass,
} from './TabSwitch.css';
import { getClassNames } from '../../util/css';
import { Stack } from '../Stack/Stack';

export const Item = forwardRef<HTMLButtonElement, ComponentProps<typeof RadioGroupPrimitive.Item>>(
  ({ className, ...props }, ref) => (
    <RadioGroupPrimitive.Item ref={ref} className={getClassNames(itemClass, className)} {...props} />
  ),
);

export const Indicator = forwardRef<HTMLDivElement, ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div ref={ref} className={getClassNames(indicatorClass, className)} {...props} />
));

// TODO: Replace component props typeof with RadioGroupItemProps from radix
type TabSwitchItemProps = ComponentProps<typeof RadioGroupPrimitive.Item> & {
  describedBy?: string;
};

const TabSwitchItem: FC<PropsWithChildren<TabSwitchItemProps>> = ({ children, value, describedBy }) => {
  const id = useId();

  return (
    <label className={labelClass} htmlFor={id}>
      <Item id={id} value={value} aria-label={reactToText(children)} aria-describedby={describedBy} tabIndex={0} />
      {children}
    </label>
  );
};

const TabSwitchComponent = forwardRef<HTMLDivElement, PropsWithChildren<TabSwitchProps>>(
  ({ children, name, value, onValueChange, defaultValue, describedBy, isOptional }, ref) => {
    const items = Children.toArray(children);
    const itemsCount = items.length;

    const indicatorPosition = items.findIndex(child => {
      if (isValidElement(child)) {
        const element = child as ReactElement<{ value: string }>;
        return value === element.props.value;
      }
      return false;
    });
    const isIndicatorVisible = !!items[indicatorPosition];

    const augmentChild = (child: ReactNode): ReactNode => {
      if (isElement(child)) {
        if (process.env.NODE_ENV === 'development') {
          if (child.type !== TabSwitchItem) {
            // eslint-disable-next-line no-console
            console.error(
              'Error: The `<TabSwitch>` component contains `children` that are invalid. `<TabSwitch>` may only contain `<TabSwitch.Item />`.',
            );
          }
        }

        return cloneElement(child as ReactElement<TabSwitchItemProps>, {
          describedBy,
        });
      }

      return child;
    };

    return (
      <RadioGroupPrimitive.Root
        ref={ref}
        className={containerClass}
        name={name}
        onValueChange={onValueChange}
        value={value}
        defaultValue={defaultValue}
        required={!isOptional}
        loop>
        <Stack direction="row" alignX="center">
          <Indicator
            className={getClassNames({ [indicatorHiddenClass]: !isIndicatorVisible })}
            style={
              assignInlineVars({
                [indicatorWidthVar]: `calc(100% / ${itemsCount})`,
                [indicatorTransformVar]: `translateX(calc(100% * ${indicatorPosition}))`,
              }) as CSSProperties
            }
          />

          {Children.map(children, augmentChild)}
        </Stack>
      </RadioGroupPrimitive.Root>
    );
  },
);

type RadioGroupProps = Pick<ComponentProps<typeof RadioGroupPrimitive.Root>, 'ref' | 'defaultValue' | 'onValueChange'>;

type TabSwitchProps = RadioGroupProps & {
  name: string;
  value: string;
  isOptional?: boolean;
  describedBy?: string;
};

TabSwitchItem.displayName = 'TabSwitch.Item';
Item.displayName = 'Item';
Indicator.displayName = 'Indicator';
TabSwitchComponent.displayName = 'TabSwitch';

// Compound components with forwardRef cause issues: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-488848720
export const TabSwitch = Object.assign({}, TabSwitchComponent, { Item: TabSwitchItem });
